/**
 * <ns-featured-collection> — grid or carousel per viewport for
 * sections/ns-featured-collection.liquid.
 *
 * Reads its layout from data attributes on the element:
 *   data-layout-mobile / data-layout-desktop  "grid" | "slider"
 *   data-per-view-mobile   cards per view below 769px (e.g. 1.5 = one card and a peek)
 *   data-per-view-tablet   cards per view from 769px
 *   data-per-view-desktop  cards per view from 1000px
 *
 * Sets data-mode="grid|slider" for the current viewport and starts or destroys
 * Swiper to match. Grid mode is plain CSS (assets/ns-featured-collection.css).
 * The controls (circle arrows + line pagination) match the comparison chart.
 *
 * Replaces the earlier window.__nsFeaturedCollectionSections bridge and its
 * inline config script: a custom element re-initialises by itself when the
 * theme editor re-renders the section.
 */

if (!customElements.get('ns-featured-collection')) {
  customElements.define(
    'ns-featured-collection',
    class NsFeaturedCollection extends HTMLElement {
      connectedCallback() {
        this.swiper = null;
        this.desktopQuery = window.matchMedia('(min-width: 769px)');
        this.onViewportChange = this.applyLayout.bind(this);
        this.desktopQuery.addEventListener('change', this.onViewportChange);
        this.applyLayout();
      }

      disconnectedCallback() {
        this.desktopQuery.removeEventListener('change', this.onViewportChange);
        this.destroySwiper();
      }

      currentLayout() {
        const layout = this.desktopQuery.matches ? this.dataset.layoutDesktop : this.dataset.layoutMobile;
        return layout === 'slider' ? 'slider' : 'grid';
      }

      applyLayout() {
        const mode = this.currentLayout();
        this.dataset.mode = mode;

        if (mode === 'grid') {
          this.destroySwiper();
          return;
        }

        if (this.swiper) return;

        if (typeof window.nsSwiperReady === 'function') {
          window.nsSwiperReady(() => this.initSwiper());
        } else {
          console.error('ns-featured-collection: nsSwiperReady is missing, carousel not started');
        }
      }

      perView(name, fallback) {
        const value = parseFloat(this.dataset[name]);
        return value > 0 ? value : fallback;
      }

      initSwiper() {
        // The viewport may have switched to grid while Swiper was loading.
        if (this.swiper || !this.isConnected || this.dataset.mode !== 'slider') return;

        const container = this.querySelector('.nsfc__swiper');
        if (!container) return;

        const controls = this.querySelector('.nsfc__controls');
        const setLocked = (locked) => {
          if (controls) controls.classList.toggle('is-locked', locked);
        };

        this.swiper = new window.Swiper(container, {
          slidesPerView: this.perView('perViewMobile', 1.5),
          slidesPerGroup: 1,
          spaceBetween: 12,
          speed: 600,
          grabCursor: true,
          watchOverflow: true,
          navigation: {
            prevEl: this.querySelector('.nsfc__prev'),
            nextEl: this.querySelector('.nsfc__next'),
          },
          pagination: {
            el: this.querySelector('.nsfc__pagination'),
            clickable: true,
            type: 'bullets',
          },
          breakpoints: {
            769: { slidesPerView: this.perView('perViewTablet', 3), spaceBetween: 20 },
            1000: { slidesPerView: this.perView('perViewDesktop', 4), spaceBetween: 20 },
          },
          on: {
            init() {
              setLocked(this.isLocked);
            },
            lock() {
              setLocked(true);
            },
            unlock() {
              setLocked(false);
            },
          },
        });

        this.classList.add('swiper-ready');
      }

      destroySwiper() {
        if (!this.swiper) return;
        this.swiper.destroy(true, true);
        this.swiper = null;
        this.classList.remove('swiper-ready');
        const controls = this.querySelector('.nsfc__controls');
        if (controls) controls.classList.remove('is-locked');
      }
    }
  );
}
